import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Button } from '@components/button';
import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTeam } from '@components/icons/team';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PasswordField } from '@components/password-field';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  DokobitSignatureOptions,
  dokobitGetCosigners,
  dokobitGetToken,
  dokobitRemoveCosigner,
  dokobitSetToken,
  useDokobitSignatureLocation,
  useUpdateDokobitSignatureLocation,
} from './api';

const DokobitHeader = () => {
  const { t } = useStore();
  return (
    <>
      <Text font="heading2" text={t('company.settings.apps.dokobit')} />
    </>
  );
};
const ConnectedHeader = () => {
  const { t } = useStore();
  return (
    <Stack space={4}>
      <DokobitHeader />
      <Text font="regular14" text={t('integrations.dokobit.settings.cosigners.description')} />
      <Separator />
    </Stack>
  );
};

export const DokobitSettingsPage = () => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();

  const [token, setToken] = useState('');
  const client = useQueryClient();

  const saveToken = useMutation(() => dokobitSetToken(token), {
    onSuccess: () => {
      showSuccess(t('integrations.dokobit.settings.token.saved'));
      client.resetQueries(['dokobit-token']);
    },
    onError: () => {
      showError(t('integrations.dokobit.settings.token.error'));
    },
  });

  const tokenQuery = useQuery({
    queryKey: ['dokobit-token'],
    queryFn: () => dokobitGetToken(),
  });
  if (tokenQuery.isError) {
    return null;
  }
  if (tokenQuery.isSuccess) {
    if (tokenQuery.data.ok) {
      return (
        <Stack space={4}>
          <ConnectedHeader />
          <SignatureLocation />
          <CosignersList />
        </Stack>
      );
    }
    return (
      <Stack space={4}>
        <DokobitHeader />
        <Text font="regular16" text={t('integrations.dokobit.settings.description')} />
        <Separator />
        <PasswordField label={t('integrations.dokobit.settings.token.label')} onChange={setToken} />
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Button
            variant="primary"
            text={t('integrations.dokobit.settings.token.submit')}
            onClick={() => saveToken.mutate()}
            isDisabled={saveToken.isLoading}
          />
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

const SignatureLocation = () => {
  const { t } = useStore();
  const signatureLocationQuery = useDokobitSignatureLocation();
  const signatureLocationMutation = useUpdateDokobitSignatureLocation();
  const [draft, setDraft] = useState('' as DokobitSignatureOptions);
  if (
    signatureLocationQuery.isLoading ||
    signatureLocationQuery.isError ||
    !signatureLocationQuery.data ||
    !signatureLocationQuery.data.signatureLocation
  ) {
    return null;
  }
  const currentLocation = signatureLocationQuery.data?.signatureLocation;

  if (!draft) {
    setDraft(currentLocation);
  }

  const signatureLocationChoices = [
    { value: 'first_top', label: t('integrations.dokobit.signaturelocation.topfirstpage') },
    { value: 'first_bottom', label: t('integrations.dokobit.signaturelocation.bottomfirstpage') },
    { value: 'last_top', label: t('integrations.dokobit.signaturelocation.toplastpage') },
    { value: 'last_bottom', label: t('integrations.dokobit.signaturelocation.bottomlastpage') },
  ];
  const isDirty = draft !== currentLocation;

  return (
    <Card space={4} variant="gray">
      <Stack space={4}>
        <Text font="bold16" text={t('integrations.taktikal.signaturelocation.label')} />
        <Select
          options={signatureLocationChoices}
          defaultValue={draft}
          key={draft}
          isClearable={false}
          onChange={(draft) => setDraft(draft as DokobitSignatureOptions)}
        />
        <CancelSaveButtons
          isOpen={isDirty}
          onCancel={() => setDraft(currentLocation)}
          onSave={() => signatureLocationMutation.mutate({ signatureLocation: draft })}
          isDisabled={signatureLocationMutation.isLoading}
        />
      </Stack>
    </Card>
  );
};

const CosignersList = () => {
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const { showModal } = useModal();

  const cosignersQuery = useQuery({
    queryKey: ['dokobit-cosigners'],
    queryFn: () => dokobitGetCosigners(),
  });
  const client = useQueryClient();

  if (cosignersQuery.isError) {
    return null;
  }
  if (cosignersQuery.isSuccess) {
    const cosigners = cosignersQuery.data;

    return (
      <Stack space={4}>
        <Stack space={0} orientation="horizontal" align={'center'} justify={'space-between'}>
          <Text font="bold16" text={t('integrations.dokobit.settings.cosigners')} />
          <IconButton
            variant="ghost-purple"
            icon={<IconAddPlus />}
            onClick={() => showModal('INTEGRATION_DOKOBIT_ADD_COSIGNER')}
          />
        </Stack>
        <Stack space={2}>
          {cosigners.length === 0 && (
            <EmptyState
              header={t('integrations.dokobit.settings.cosigners.none.header')}
              content={t('integrations.dokobit.settings.cosigners.none.content')}
              icon={<IconTeam size={80} />}
            />
          )}
          {cosigners.map((cosigner) => (
            <Card space={4} key={cosigner.id}>
              <CosignerContainer>
                <Stack space={1}>
                  <Text font="bold14" text={cosigner.name} />
                  <Text font="regular14" text={cosigner.email} color="purple60" />
                </Stack>
                <Stack space={1}>
                  <Text font="regular14" text={cosigner.ssn} color="purple60" />
                  <Text font="regular14" text={cosigner.phone} color="purple60" />
                </Stack>
                <Actions>
                  <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
                    <Menu.Item
                      label={t('integrations.dokobit.settings.cosigners.edit')}
                      icon={<IconEdit />}
                      onSelect={() => {
                        showModal('INTEGRATION_DOKOBIT_EDIT_COSIGNER', { cosigner });
                      }}
                    />
                    <Menu.Item
                      label={t('integrations.dokobit.settings.cosigners.delete')}
                      icon={<IconTrash />}
                      variant="danger"
                      onSelect={() => {
                        dokobitRemoveCosigner(cosigner.id.toString()).then(() => {
                          showSuccess(t('integrations.dokobit.settings.cosigners.remove.success'));
                          client.resetQueries(['dokobit-cosigners']);
                        });
                      }}
                    />
                  </Menu>
                </Actions>
              </CosignerContainer>
            </Card>
          ))}
        </Stack>
      </Stack>
    );
  }
  return <Spinner />;
};

const CosignerContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '3fr 2fr 1fr',
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
});
